// Special order printouts: the customer's order receipt (80 mm thermal) and the store's sticker
// for the back of the phone (DYMO label, or the receipt printer). Neither shows the supplier or
// what the part cost us.
import { get } from '../lib/api';
import { formatCents, formatRatePpm } from '../../shared/money';
import { formatDateTime, formatPhone, zone } from '../../shared/format';
import type { OrderLabelSize } from '../../shared/parts';
import { Barcode, type PaperSize, type PrintFn } from '../ui/print';
import type { PrintOut } from '../lib/printing';
import { ReceiptMark } from '../ui/legal';
import { ORDER_BANNER, orderArrival, orderPayment } from '../../shared/receipts';
import { PayStampView, ReceiptHeader, ReceiptPolicies, SiteLine, type StoreInfo } from './Receipt';

export interface OrderPrint {
  business_name: string;
  logo_url: string;
  website: string;
  terms: string;
  /** The store's special order policies, one per line. */
  policies?: string | null;
  label_size: OrderLabelSize;
  store: StoreInfo;
  number: string;
  status?: string;
  status_label: string;
  created_at: number;
  employee: string;
  customer: { name: string; phone: string | null };
  description: string;
  price_cents: number;
  taxable: boolean;
  tax_rate_ppm: number;
  tax_cents: number;
  /** Paid on a sale a manager took the tax off. */
  tax_removed?: boolean;
  total_cents: number;
  paid: { receipt_no: number | null; amount_cents: number; paid_at: number | null; refunded: boolean } | null;
  pay_plan: 'now' | 'pickup' | null;
  device_left: boolean;
  device: string | null;
  /** What was left with the shop: phone, computer, vacuum or vehicle. */
  item?: string;
  expected: string | null;
  delivered_at: number | null;
}

const itemOf = (p: OrderPrint) => p.item || 'phone';
const capital = (s: string) => s[0].toUpperCase() + s.slice(1);

const shortDay = (ms: number) => new Intl.DateTimeFormat('en-US', { timeZone: zone.current, month: 'numeric', day: 'numeric' }).format(new Date(ms));
const etaDay = (iso: string | null) => {
  const m = /^\d{4}-(\d{2})-(\d{2})/.exec(iso ?? '');
  return m ? `${Number(m[1])}/${Number(m[2])}` : '';
};

/**
 * The customer's copy: on top, white on black, PART ORDERED FOR THIS CUSTOMER (whoever reads it
 * at the counter knows right away); the order number to bring back; the day it's expected, big;
 * what was ordered, the price, and paid or not.
 */
export function OrderReceiptPaper({ p }: { p: OrderPrint }) {
  const arrival = orderArrival(p);
  return (
    <div className="receipt" aria-label={`Order receipt ${p.number}`}>
      <ReceiptHeader logo={p.logo_url} business={p.business_name} store={p.store} website={p.website} />
      <div className="order-banner">{ORDER_BANNER}</div>
      <div className="c strong">SPECIAL ORDER</div>
      <div className="ticket-no">{p.number}</div>
      <div className="bar"><Barcode value={p.number} /></div>
      <div className="arrive">
        <div className="l">{arrival.label}</div>
        {arrival.big ? <div className="d">{arrival.big}</div> : null}
        {arrival.note ? <div className="n">{arrival.note}</div> : null}
      </div>
      <dl className="meta">
        <dt>Ordered</dt><dd>{formatDateTime(p.created_at)}</dd>
        <dt>Customer</dt><dd>{p.customer.name}</dd>
        {p.customer.phone ? <><dt>Phone</dt><dd>{formatPhone(p.customer.phone)}</dd></> : null}
        {p.employee ? <><dt>Served by</dt><dd>{p.employee}</dd></> : null}
      </dl>
      <hr />
      <table className="rtable">
        <thead><tr><th>ITEM</th><th className="r">PRICE</th></tr></thead>
        <tbody><tr><td>{p.description}</td><td className="r">{formatCents(p.price_cents)}</td></tr></tbody>
      </table>
      <hr />
      {p.taxable ? <div className="rl"><span>{p.tax_removed ? 'Tax removed' : `Tax (${formatRatePpm(p.tax_rate_ppm)})`}</span><span>{formatCents(p.tax_cents)}</span></div> : null}
      <div className="rl tot"><span>TOTAL</span><span>{formatCents(p.total_cents)}</span></div>
      <hr />
      <PayStampView pay={orderPayment(p)} />
      <hr />
      <div className="rl"><span>Your {itemOf(p)}</span><span className="strong">{p.device_left ? 'Left with us' : 'You kept it'}</span></div>
      {p.device_left && p.device ? <div className="dim">{p.device}</div> : null}
      <hr />
      <div className="c strong">Bring this receipt to pick up your order.</div>
      {p.terms ? <div className="terms" style={{ marginTop: 6 }}>{p.terms}</div> : null}
      {p.store.phone ? <div className="c dim" style={{ marginTop: 6 }}>Questions? {formatPhone(p.store.phone)}</div> : null}
      <ReceiptPolicies title="ORDER POLICIES" text={p.policies} />
      <SiteLine website={p.website} />
      <ReceiptMark />
    </div>
  );
}

/** The store's copy, stuck on the phone (or the computer, vacuum or vehicle's tag): order number, whose it is, what's coming and if it's paid. */
export function OrderStickerPaper({ p }: { p: OrderPrint }) {
  const paid = !!p.paid && !p.paid.refunded;
  const money = paid ? 'PAID' : `DUE ${formatCents(p.total_cents)}`;
  const dates = [`In ${shortDay(p.created_at)}`, p.expected ? `ETA ${etaDay(p.expected)}` : null, p.device_left ? `${capital(itemOf(p))} here` : null].filter(Boolean).join(' · ');
  if (p.label_size === 'thermal') {
    return (
      <div className="receipt osticker-thermal" aria-label={`${capital(itemOf(p))} sticker ${p.number}`}>
        <div className="c strong">STORE COPY · KEEP WITH THE {itemOf(p).toUpperCase()}</div>
        <div className="ticket-no">{p.number}</div>
        <div className="big">{money}</div>
        <div className="strong">{p.customer.name}{p.customer.phone ? ` · ${formatPhone(p.customer.phone)}` : ''}</div>
        <div>{p.description}</div>
        {p.device ? <div className="dim">{p.device}</div> : null}
        <div className="dim">{dates}</div>
        <div className="bar"><Barcode value={p.number} /></div>
      </div>
    );
  }
  if (p.label_size === 'brother_62x100') {
    return (
      <div className="osticker-big" aria-label={`${capital(itemOf(p))} sticker ${p.number}`}>
        <div className="ob-top"><span>SPECIAL ORDER</span><span>{shortDay(p.created_at)}</span></div>
        <div className="ob-no">{p.number}</div>
        <div className="ob-bar"><Barcode value={p.number} height={34} /></div>
        <div className={`ob-pay ${paid ? 'paid' : 'due'}`}>{money}</div>
        <div className="ob-cust">{p.customer.name}</div>
        {p.customer.phone ? <div className="ob-phone">{formatPhone(p.customer.phone)}</div> : null}
        <div className="ob-item">{p.description}</div>
        {p.device ? <div className="ob-dev">{capital(itemOf(p))}: {p.device}</div> : null}
        <div className="ob-dates">{dates}</div>
        <div className="ob-foot">STORE COPY · KEEP WITH THE {itemOf(p).toUpperCase()}</div>
      </div>
    );
  }
  return (
    <div className={`osticker ${p.label_size === 'dymo_30252' ? 's30252' : 's30334'}`} aria-label={`${capital(itemOf(p))} sticker ${p.number}`}>
      <div className="os-top">
        <span className="os-no">{p.number}</span>
        <span className={`os-pay ${paid ? 'paid' : 'due'}`}>{money}</span>
      </div>
      <div className="os-cust">{p.customer.name}{p.customer.phone ? ` · ${formatPhone(p.customer.phone)}` : ''}</div>
      <div className="os-item">{p.description}</div>
      <div className="os-dates">{dates}</div>
      <div className="os-bar"><Barcode value={p.number} height={30} /></div>
    </div>
  );
}

export function stickerPaper(size: OrderLabelSize): PaperSize {
  return size === 'thermal' ? 'thermal' : size === 'brother_62x100' ? 'phone-label' : size === 'dymo_30252' ? 'sticker-wide' : 'sticker';
}

/**
 * Prints the customer's receipt, the phone sticker, or both. With the store's receipt printer set
 * up (PrintNode, `printOut`), the receipt prints there with no print window. Otherwise, when the
 * sticker also goes on the receipt printer, both come out of one print: two pages, cut between them.
 */
export async function printOrder(print: PrintFn, orderId: string, which: 'receipt' | 'sticker' | 'both', printOut?: (o: PrintOut) => Promise<void>) {
  const p = await get<OrderPrint>(`/api/orders/${orderId}/print`);
  const stickerOn = stickerPaper(p.label_size);
  if (printOut && which !== 'sticker') {
    await printOut({ kind: 'order_receipt', refId: orderId, title: `Order ${p.number} receipt`, browser: () => print(<OrderReceiptPaper p={p} />, 'thermal') });
    if (which === 'both') print(<OrderStickerPaper p={p} />, stickerOn);
    return;
  }
  if (which === 'both' && stickerOn === 'thermal') return print([<OrderReceiptPaper p={p} />, <OrderStickerPaper p={p} />], 'thermal');
  if (which !== 'sticker') print(<OrderReceiptPaper p={p} />, 'thermal');
  if (which !== 'receipt') print(<OrderStickerPaper p={p} />, stickerOn);
}
